export interface Outline {
    points: number[][];
    totalPoints: number[][];
    boundsX: number;
    boundsY: number;
    boundsWidth: number;
    boundsHeight: number;
}

export const outline = () => {
    let imageData!: ImageData;

    // lookup tables for marching directions
    const d3_geom_contourDx = [1, 0, 1, 1, -1, 0, -1, 1, 0, 0, 0, 0, -1, 0, -1, NaN];
    const d3_geom_contourDy = [0, -1, 0, 0, 0, -1, 0, 0, 1, -1, 1, 1, 0, -1, 0, NaN];

    // Implementation

    const findStartPoint = () => {
        let x = 0,
            y = 0;

        // search for a starting point; begin at origin
        // and proceed along outward-expanding diagonals
        while (true) {
            if (!isPixelTransparent(x, y)) {
                return { x, y };
            }

            x++;

            if (x >= imageData.width) {
                x = 0;
                y++;
            }

            if (y >= imageData.height) return null;
        }
    };

    const isPixelTransparent = (x: number, y: number) => {
        let index = (y * imageData.width + x) * 4 + 3;
        let alpha = imageData.data[index];
        return alpha < 128;
    };

    const setPixelAlpha = (x: number, y: number, a: number) => {
        let idx = (y * imageData.width + x) * 4;

        imageData.data[idx] = 0; // R
        imageData.data[idx + 1] = 0; // G
        imageData.data[idx + 2] = 0; // B
        imageData.data[idx + 3] = a; // A
    };

    const isPointInside = (point: number[], vs: number[][]) => {
        // ray-casting algorithm based on
        // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

        let x = point[0],
            y = point[1];

        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            let xi = vs[i][0],
                yi = vs[i][1];
            let xj = vs[j][0],
                yj = vs[j][1];

            let intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

            if (intersect) inside = !inside;
        }

        return inside;
    };

    const cleanPoints = (points: number[][]) => {
        let cleaned: number[][] = [];
        for (let i = 0; i < points.length; i++) {
            if (cleaned.length > 0) {
                let lastPoint = cleaned[cleaned.length - 1];

                if (Math.abs(lastPoint[0] - points[i][0]) < 1 && Math.abs(lastPoint[1] - points[i][1]) < 1) {
                    continue;
                }
            }

            cleaned.push(points[i]);
        }

        console.log('Cleaned points ' + points.length + '=>' + cleaned.length);

        return cleaned;
    };

    const countour = (start?: { x: number; y: number }): Outline | null => {
        const startPoint = start || findStartPoint(); // starting point

        // Completly transparent image
        if (startPoint == null) return null;

        let c: number[][] = [], // contour polygon
            x = startPoint.x, // current x position
            y = startPoint.y, // current y position
            dx = 0, // next x direction
            dy = 0, // next y direction
            pdx = NaN, // previous x direction
            pdy = NaN, // previous y direction
            i = 0;

        let boundsX: number | null = 0;
        let boundsY: number | null = 0;
        let boundsW: number | null = 1;
        let boundsH: number | null = 1;

        do {
            // determine marching squares index
            i = 0;

            if (!isPixelTransparent(x - 1, y - 1)) i += 1;
            if (!isPixelTransparent(x, y - 1)) i += 2;
            if (!isPixelTransparent(x - 1, y)) i += 4;
            if (!isPixelTransparent(x, y)) i += 8;

            // determine next direction
            if (i === 6) {
                dx = pdy === -1 ? -1 : 1;
                dy = 0;
            } else if (i === 9) {
                dx = 0;
                dy = pdx === 1 ? -1 : 1;
            } else {
                dx = d3_geom_contourDx[i];
                dy = d3_geom_contourDy[i];
            }

            if (isNaN(dx) || isNaN(dy)) throw 'Cannot track the path';

            // update contour polygon
            if (dx != pdx && dy != pdy) {
                c.push([x, y]);
                pdx = dx;
                pdy = dy;

                if (boundsX == null || x < boundsX) boundsX = x;

                if (boundsY == null || y < boundsY) boundsY = y;

                if (boundsW == null || x > boundsW) boundsW = x;

                if (boundsH == null || y > boundsH) boundsH = y;
            }

            x += dx;
            y += dy;

            if (x > imageData.width || y > imageData.height) throw 'Cannot track the path';
        } while (startPoint.x != x || startPoint.y != y);

        return {
            points: cleanPoints(c),
            totalPoints: c,
            boundsX: boundsX ?? 0,
            boundsY: boundsY ?? 0,
            boundsWidth: boundsW ?? 1,
            boundsHeight: boundsH ?? 1
        };
    };

    /**
     * Validate the mask file (now only transparent border)
     */
    const validateMask = () => {
        let failed = false;

        // Make the found outline trasparent and invert colors inside
        for (let x = 0; x < imageData.width; x++) {
            //console.log("Swapping [" + x + ", " + y + "]");
            let transparent1 = isPixelTransparent(x, 0);
            let transparent2 = isPixelTransparent(x, imageData.height - 1);
            if (!transparent1 || !transparent2) {
                failed = true;
                break;
            }
        }

        for (let y = 0; y < imageData.height; y++) {
            //console.log("Swapping [" + x + ", " + y + "]");
            let transparent1 = isPixelTransparent(0, y);
            let transparent2 = isPixelTransparent(imageData.width - 1, y);

            if (!transparent1 || !transparent2) {
                failed = true;
                break;
            }
        }

        return {
            success: !failed,
            error: failed ? 'NO_TRANSPARENT_BORDER' : null
        };
    };

    const parse = (data: ImageData) => {
        if (data == null) throw 'Data cannot be null';

        let outlines: Outline[] = [];
        imageData = data;

        const validationResult = validateMask();

        if (!validationResult.success) {
            return validationResult;
        }

        for (let i = 0; i < 1000; i++) {
            console.log('Loop ' + i);
            let outline: Outline | null = null;

            try {
                outline = countour();
            } catch (ex) {
                return {
                    success: false,
                    error: 'FAIL'
                };
            }

            if (outline == null || outline.points.length == 0) break;

            console.log('Path ' + i + ' length ' + outline.points.length);
            console.log('%o', outline);

            outlines.push(outline);

            // Make the found outline trasparent and invert colors inside
            for (let x = outline.boundsX; x < outline.boundsX + outline.boundsWidth; x++) {
                for (let y = outline!.boundsY; y < outline!.boundsY + outline!.boundsHeight; y++) {
                    if (isPointInside([x, y], outline.totalPoints)) {
                        //console.log("Swapping [" + x + ", " + y + "]");
                        let transparent = isPixelTransparent(x, y);
                        let a = transparent ? 255 : 0;

                        setPixelAlpha(x, y, a);
                    }
                }
            }
        }

        return {
            success: true,
            outlines
        };
    };

    self.onmessage = (e: { data: ImageData }) => {
        var imageData: ImageData = e.data;

        console.log('Outline parser web worker start...');

        var result = parse(imageData);

        console.log('Outline parser web worker finished.');

        // Cast as any because postMessage in web worker has different signature
        (self.postMessage as any)(result);
    };
};
